import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Chip,
  Divider,
  Fade,
  Slide,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, 
  Receipt, 
  CheckCircle, 
  Restaurant, 
  LocalShipping, 
  ShoppingBag,
  AccessTime,
  Phone,
  LocationOn,
  Star,
  Celebration,
  ArrowBack,
  TableRestaurant,
  CheckCircleOutline
} from '@mui/icons-material';
import { useCustomTheme } from '../../contexts/ThemeContext';

const OrderSuccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { isDarkMode } = useCustomTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const orderData = location.state?.orderData;

  return (
    <Box>
      {/* Simple Navigation Bar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : 'rgba(45, 27, 20, 0.95)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 20px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 1, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              color="inherit"
              onClick={() => navigate('/')}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                fontWeight: 600,
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}
            >
              Order Confirmed!
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ mt: 8, minHeight: '100vh', bgcolor: 'background.default' }}>
        <Container maxWidth="md" sx={{ py: 4 }}>
          {/* Success Header */}
          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              {/* Success Icon */}
              <Box sx={{ 
                position: 'relative',
                display: 'inline-block',
                mb: 4
              }}>
                <Box sx={{
                  width: { xs: 120, md: 140 },
                  height: { xs: 120, md: 140 },
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  boxShadow: '0 8px 32px rgba(76, 175, 80, 0.3)',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.05)' },
                    '100%': { transform: 'scale(1)' }
                  }
                }}>
                  <CheckCircleOutline 
                    sx={{ 
                      fontSize: { xs: 70, md: 80 }, 
                      color: 'white',
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                      position: 'absolute'
                    }} 
                  />
                  <CheckCircle 
                    sx={{ 
                      fontSize: { xs: 40, md: 50 }, 
                      color: 'white',
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                      position: 'absolute'
                    }} 
                  />
                </Box>
              </Box>
              
              {/* Success Message */}
              <Typography 
                variant={isMobile ? 'h4' : 'h3'} 
                fontWeight="bold" 
                sx={{ 
                  mb: 2,
                  color: 'success.main',
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                Order Confirmed!
              </Typography>
              
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ 
                  mb: 4, 
                  maxWidth: 500, 
                  mx: 'auto',
                  fontWeight: 400,
                  lineHeight: 1.6
                }}
              >
                Thank you for your order! Your meal is being prepared with care.
              </Typography>
            </Box>
          </Fade>

          {/* Order Details Card */}
          <Slide in timeout={1000} direction="up">
            <Card 
              elevation={4} 
              sx={{ 
                mb: 4,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid',
                borderColor: 'success.light',
                overflow: 'hidden'
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ 
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1
                  }}>
                    <TableRestaurant />
                    Order Summary
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                </Box>
              
              {orderData && (
                <Box sx={{ textAlign: 'left' }}>
                  {/* Order Type Chip */}
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Chip
                      icon={<TableRestaurant />}
                      label="Table Order"
                      color="primary"
                      variant="filled"
                      sx={{ 
                        fontSize: '1rem',
                        py: 2,
                        px: 3,
                        height: 'auto',
                        '& .MuiChip-icon': { fontSize: '1.2rem' }
                      }}
                    />
                  </Box>
                  
                  {/* Order Details */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" fontWeight="600" gutterBottom sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: 'text.primary'
                    }}>
                      <Star sx={{ color: '#ff9800' }} />
                      Order Details
                    </Typography>
                    
                    <Box sx={{ 
                      bgcolor: 'grey.50', 
                      p: 3, 
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'grey.200'
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Order Number:</Typography>
                        <Typography variant="body2" fontWeight="600">
                          {orderData.order_number || 'TBL-001'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Customer:</Typography>
                        <Typography variant="body2" fontWeight="600">
                          {orderData.customer_name || 'Guest'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Table:</Typography>
                        <Typography variant="body2" fontWeight="600">
                          Table {orderData.table_number || 'N/A'}
                        </Typography>
                      </Box>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="h6" color="text.primary">Total Amount:</Typography>
                        <Typography variant="h6" fontWeight="bold" color="primary.main">
                          S${orderData.total_amount || '0.00'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Slide>

          {/* Next Steps Section */}
          <Slide in timeout={1200} direction="up">
            <Card 
              elevation={2} 
              sx={{ 
                mb: 4,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #e8f5e8 0%, #f0f8f0 100%)',
                border: '1px solid',
                borderColor: 'success.light'
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ 
                  textAlign: 'center',
                  color: 'success.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}>
                  <AccessTime />
                  What's Next?
                </Typography>
                
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" color="text.primary" sx={{ 
                    mb: 2, 
                    fontWeight: 500,
                    lineHeight: 1.6
                  }}>
                    🍽️ Your order has been sent to the kitchen and will be prepared fresh for your table.
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    mb: 3,
                    fontStyle: 'italic'
                  }}>
                    💡 Our staff will bring your order directly to your table when ready.
                  </Typography>
                  
                  <Chip
                    label="Estimated Time: 15-25 minutes"
                    color="success"
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.9rem',
                      py: 1,
                      px: 2,
                      height: 'auto'
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Slide>

          {/* Action Buttons */}
          <Slide in timeout={1400} direction="up">
            <Box sx={{ 
              display: 'flex', 
              gap: 3, 
              justifyContent: 'center',
              flexDirection: isMobile ? 'column' : 'row',
              maxWidth: 500,
              mx: 'auto'
            }}>
              <Button
                variant="contained"
                startIcon={<Home />}
                onClick={() => navigate('/')}
                size="large"
                sx={{ 
                  minWidth: isMobile ? '100%' : 200,
                  py: 2,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                  boxShadow: '0 4px 16px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Back to Home
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<Receipt />}
                onClick={() => navigate('/orders')}
                size="large"
                sx={{ 
                  minWidth: isMobile ? '100%' : 200,
                  py: 2,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  borderWidth: 2,
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: 'primary.dark',
                    backgroundColor: 'primary.50',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 16px rgba(25, 118, 210, 0.2)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                View Orders
              </Button>
            </Box>
          </Slide>
        </Container>
      </Box>
    </Box>
  );
};

export default OrderSuccessPage;
